import { FC, useContext, useEffect, useRef, useState } from "react";

import { WebsocketStatusType } from "../../shared/types/pubsub";
import { AUTH_ERRORS } from "../../shared/types/websocket-auth";

import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import {
  CloudConnectedIcon,
  CloudConnectingIcon,
  CloudDisconnectedIcon,
  RefreshIcon,
} from "mds/icons";
import { IconProps } from "mds/utils/images";
import { PubsubWebsocketContext } from "providers/PubsubWebsocketProvider";
import { ShareDBContext } from "providers/ShareDBProvider";
import { useAppSelector } from "store/index";
import { selectLastOpAt, selectLastOpSavedAt, selectOpError } from "store/selectors";
import { getCombinedConnectionStatus } from "utils/connection-status";
import { isLocalEnv } from "utils/env";
import { useBrowserOnline } from "utils/hooks/use-browser-online";

// After this threshold the connection is considered delayed and an
// error banner is shown to the user.
const SHAREDB_DELAY_THRESHOLD_MS = 5000;

type ConnectionStateType = "connected" | "connecting" | "disconnected" | "saving" | "offline";

/**
 * Active states for the saving status of a document / sharedb operation.
 * null indicates that the document is saved and no special status should be shown.
 */

type NonNullDocSavingStatusType = "delayed" | "saving";
type DocSavingStatusType = NonNullDocSavingStatusType | null;

const SAVING_STATUS: Record<string, DocSavingStatusType> = {
  DELAYED: "delayed",
  SAVING: "saving",
};

const WEBSOCKET_STATE_TO_ICON_STATE: Record<
  WebsocketStatusType | NonNullDocSavingStatusType,
  ConnectionStateType
> = {
  connected: "connected",
  // Normalize transient states so integration screenshots don't depend on connection or save timing.
  delayed: isLocalEnv ? "connected" : "connecting",
  saving: isLocalEnv ? "connected" : "saving",
  connecting: isLocalEnv ? "connected" : "connecting",
  disconnected: "disconnected",
  stopped: "disconnected",
  closed: "disconnected",
  "401": "disconnected",
  failed: "disconnected",
};

const ICON_STATE_TO_COLOR: Record<ConnectionStateType, string> = {
  connecting: "text-orange",
  connected: "text-green",
  disconnected: "text-red",
  offline: "text-red",
  saving: "text-blue",
};

const ICON_STATE_TO_ICON_COMPONENT: Record<ConnectionStateType, FC<IconProps>> = {
  saving: RefreshIcon,
  connecting: CloudConnectingIcon,
  connected: CloudConnectedIcon,
  disconnected: CloudDisconnectedIcon,
  offline: CloudDisconnectedIcon,
};

type ConnectionIconProps = {
  iconState: ConnectionStateType;
  classOverride?: string;
  showText?: boolean;
};

export const ConnectionIcon = ({ iconState, classOverride, showText }: ConnectionIconProps) => {
  const Icon = ICON_STATE_TO_ICON_COMPONENT[iconState];
  const colorClass = ICON_STATE_TO_COLOR[iconState];
  const tooltipMessage = t(`websocket.saving_indicator.${iconState}`);

  const icon = (
    <div className={iconState === "saving" ? "animate-rotate-clockwise" : undefined}>
      <IconText
        iconStart={<Icon className={classOverride || colorClass} size="l" />}
        text={showText ? tooltipMessage : undefined}
      />
    </div>
  );

  if (showText) {
    return icon;
  }

  return (
    <TooltipTrigger content={tooltipMessage} label={t("websocket.saving_indicator.aria_label")}>
      {icon}
    </TooltipTrigger>
  );
};

type ConnectionStatusIndicatorProps = {
  className?: string;
  iconColorClass?: string;
  showText?: boolean;
};

type OverallConnectionStatusParams = {
  shareDBStatus: WebsocketStatusType | null;
  pubsubStatus: WebsocketStatusType;
  savingStatus: DocSavingStatusType;
  opError: string | null;
  isOverwriteError: boolean;
  isOnline: boolean;
};

/**
 * Represents both the current connection status to the websocket/server,
 * and also displays saving status indication for any document currently open.
 *
 * Connection errors are surfaced via the full-width connection banner in GlobalBanners.tsx.
 * This indicator only shows the icon state in the header.
 */
const getOverallConnectionStatus = ({
  shareDBStatus,
  pubsubStatus,
  savingStatus,
  opError,
  isOverwriteError,
  isOnline,
}: OverallConnectionStatusParams): ConnectionStateType => {
  if (!isOnline) {
    return "offline";
  }

  // Op errors take highest priority (except overwrite errors)
  if (opError && !isOverwriteError) {
    return "disconnected";
  }

  // Saving status takes priority over connection status
  if (savingStatus) {
    return WEBSOCKET_STATE_TO_ICON_STATE[savingStatus];
  }

  return WEBSOCKET_STATE_TO_ICON_STATE[getCombinedConnectionStatus(shareDBStatus, pubsubStatus)];
};

export const ConnectionStatusIndicator: FC<ConnectionStatusIndicatorProps> = ({
  className,
  iconColorClass,
  showText,
}) => {
  const shareDBContext = useContext(ShareDBContext);
  const pubsubContext = useContext(PubsubWebsocketContext);
  // ShareDB may not be available on pages like home page (only mounted in section routes)
  const shareDBStatus = shareDBContext?.shareDBStatus ?? null;
  const pubsubStatus = pubsubContext?.pubsubStatus ?? "connecting";
  const [savingStatus, setSavingStatus] = useState<DocSavingStatusType>(null);
  const lastOpAt = useAppSelector(selectLastOpAt);
  const lastOpSavedAt = useAppSelector(selectLastOpSavedAt);
  const opError = useAppSelector(selectOpError);
  const delayTimeout = useRef<NodeJS.Timeout>(null);
  const minUiFlickerTimeout = useRef<NodeJS.Timeout>(null);
  const isOverwriteError = opError === AUTH_ERRORS.OVERWRITE_DISALLOWED;
  const isOnline = useBrowserOnline();

  const connectionState = getOverallConnectionStatus({
    shareDBStatus,
    pubsubStatus,
    savingStatus,
    opError,
    isOverwriteError,
    isOnline,
  });

  useEffect(() => {
    if (!lastOpAt) {
      return;
    }
    if (delayTimeout.current) {
      clearTimeout(delayTimeout.current);
    }
    if (minUiFlickerTimeout.current) {
      clearTimeout(minUiFlickerTimeout.current);
    }
    if (lastOpSavedAt && lastOpAt < lastOpSavedAt) {
      minUiFlickerTimeout.current = setTimeout(() => {
        setSavingStatus(null);
      }, 1000);
    } else {
      setSavingStatus((status) => status || SAVING_STATUS.SAVING);
      delayTimeout.current = setTimeout(() => {
        setSavingStatus(SAVING_STATUS.DELAYED);
      }, SHAREDB_DELAY_THRESHOLD_MS);
    }
    return () => {
      if (delayTimeout.current) {
        clearTimeout(delayTimeout.current);
      }
      if (minUiFlickerTimeout.current) {
        clearTimeout(minUiFlickerTimeout.current);
      }
    };
  }, [lastOpSavedAt, lastOpAt]);

  useEffect(() => {
    if ((connectionState !== "disconnected" && connectionState !== "offline") || isOverwriteError) {
      return;
    }
    setSavingStatus(null);
  }, [opError, connectionState, isOverwriteError]);

  return (
    <div className={className}>
      <ConnectionIcon
        classOverride={iconColorClass}
        iconState={connectionState}
        showText={showText}
      />
    </div>
  );
};
